import { createElement, useCallback, useRef } from "react";
import { useStore } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "app/hooks";
import type { RootState } from "app/store";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  setOpen as setConfirmDialogOpen,
  setContent,
  setOnChange,
  setOptions,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import {
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import { schedulingSettingsApi } from "features/scheduling-v3/data/api/scheduling-settings.api";
import {
  OPT_IN_NAMESPACE,
  writeGuideSeen,
  writeNotNow,
  writeOptInDocument,
  writeOptOut
} from "features/scheduling-v3/domain/settings-document";
import { selectSelectedDate } from "features/scheduling-v3/state/scheduler-v3.selectors";
import { setSelectedDate } from "features/scheduling-v3/state/scheduler-v3.slice";
import {
  applyDateRangeFilter,
  selectDateRangeValues
} from "features/scheduling/scheduler-slice";

import {
  type OptOutAnswer,
  OptOutReason,
  isOptOutAnswerComplete
} from "./opt-out-reason.component";
import {
  type SchedulerSwitchSource,
  trackSchedulerOfferNotNow,
  trackSchedulerOptIn,
  trackSchedulerOptOut
} from "./scheduler-choice-telemetry";
import {
  isClassicPath,
  isV3Path,
  toClassicPath,
  toV3Path
} from "./scheduler-paths";
import { useSchedulerChoice } from "./use-scheduler-choice.hook";

const SWITCH_FAILED = "Couldn't switch schedulers. Try again.";

/**
 * ENG-1981: every way of switching schedulers goes through here — the banner, the account
 * menu's switch and the "Switch to classic scheduler" link in V3's side panel. Each switch is saved first; only a
 * saved switch moves the page, carries the day across and is recorded.
 *
 * It also records that the person has seen the new scheduler's guide (ENG-2384): the same
 * document, so the same rule about building on the server's record applies.
 *
 * The saved document goes into the choice's cache BEFORE navigating: the route gate reads
 * that cache, and a stale "classic" there would send the person straight back.
 */
export function useSwitchScheduler() {
  const dispatch = useAppDispatch();
  // Read at the moment of switching, not subscribed: the dates only matter then.
  const store = useStore<RootState>();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector(selectCurrentUser);
  const practice = useAppSelector(selectCurrentPractice);
  const { document, fromServer } = useSchedulerChoice();
  const [putSettings] =
    schedulingSettingsApi.usePutSchedulingSettingsMutation();

  // Read at the moment of writing, not when a dialog opened.
  const latest = useRef({ document, fromServer });
  latest.current = { document, fromServer };

  /**
   * Every write builds on the server's own record. When the choice came from the D14 mirror
   * or from a failed read, that record is not in hand, so it is read first (joining a read
   * already in flight); if it cannot be read, nothing is written — writing the stand-in back
   * would erase the person's "Not now" count and every reason they gave for leaving.
   *
   * `quiet` skips the "Couldn't switch" snackbar, for a write that is not a switch.
   */
  const save = useCallback(
    async (
      build: (base: unknown) => Record<string, unknown>,
      { quiet = false }: { quiet?: boolean } = {}
    ): Promise<boolean> => {
      const userId = user?.id;
      const practiceId = practice?.id;
      if (userId == null || practiceId == null) return false;
      try {
        const base = latest.current.fromServer
          ? latest.current.document
          : await dispatch(
              schedulingSettingsApi.endpoints.getSchedulingOptIn.initiate(
                { userId, practiceId },
                { subscribe: false, forceRefetch: true }
              )
            ).unwrap();
        const next = build(base);
        const saved = await putSettings({
          namespace: OPT_IN_NAMESPACE,
          practiceId,
          document: next
        }).unwrap();
        dispatch(
          schedulingSettingsApi.util.upsertQueryData(
            "getSchedulingOptIn",
            { userId, practiceId },
            saved.document
          )
        );
        return true;
      } catch {
        if (!quiet) {
          dispatch(setCustomMessage(SWITCH_FAILED));
          dispatch(setSnackbarOpen(true));
        }
        return false;
      }
    },
    [dispatch, putSettings, user?.id, practice?.id]
  );

  const optIn = useCallback(
    async (source: SchedulerSwitchSource) => {
      const now = new Date().toISOString();
      if (!(await save((base) => writeOptInDocument(base, true, now)))) return;
      trackSchedulerOptIn(source);
      if (!isClassicPath(location.pathname)) return;
      dispatch(
        setSelectedDate(selectDateRangeValues(store.getState()).selectedDate)
      );
      navigate(toV3Path(location.pathname, location.search));
    },
    [save, location.pathname, location.search, dispatch, store, navigate]
  );

  const optOut = useCallback(
    (source: SchedulerSwitchSource) => {
      let answer: OptOutAnswer = { reason: "", detail: "" };
      // ENG-2210: the reason is required, so "Switch to classic" stays disabled until the
      // answer is complete. The dialog's content never re-renders (it lives in the store),
      // so the button is enabled from here, through the dialog's options.
      let disabled = true;
      const showOptions = () =>
        dispatch(
          setOptions({
            actionButtonText: "Switch to classic",
            actionButtonDisabled: disabled
          })
        );
      const confirm = async () => {
        // The button is disabled until the answer is complete; this is the backstop, so a
        // switch can never be saved or recorded without a reason.
        if (!isOptOutAnswerComplete(answer)) return;
        const { reason, detail } = answer;
        const saved = await save((base) =>
          writeOptOut(base, {
            at: new Date().toISOString(),
            practiceId: practice?.id ?? 0,
            reason,
            detail,
            source
          })
        );
        if (!saved) return;
        trackSchedulerOptOut(source, reason);
        if (!isV3Path(location.pathname)) return;
        dispatch(applyDateRangeFilter(selectSelectedDate(store.getState())));
        navigate(toClassicPath(location.pathname, location.search));
      };
      dispatch(setTitle("Switch back to the classic scheduler?"));
      dispatch(
        setContent(
          createElement(OptOutReason, {
            onChange: (next: OptOutAnswer) => {
              answer = next;
              const nextDisabled = !isOptOutAnswerComplete(next);
              if (nextDisabled === disabled) return;
              disabled = nextDisabled;
              showOptions();
            }
          })
        )
      );
      showOptions();
      dispatch(setOnChange(confirm));
      dispatch(setConfirmDialogOpen(true));
    },
    [
      dispatch,
      practice?.id,
      save,
      location.pathname,
      location.search,
      store,
      navigate
    ]
  );

  const notNow = useCallback(async () => {
    if (await save((base) => writeNotNow(base, new Date().toISOString()))) {
      trackSchedulerOfferNotNow();
    }
  }, [save]);

  // A failed save is silent: the callout hides for this visit anyway and is back next time.
  const markGuideSeen = useCallback(
    () =>
      save((base) => writeGuideSeen(base, new Date().toISOString()), {
        quiet: true
      }),
    [save]
  );

  return { optIn, optOut, notNow, markGuideSeen };
}
